<script setup lang="ts" generic="T extends Record<string, unknown>">
import {
  computed,
  ref,
  watch,
  type Ref,
  type Directive,
  onMounted,
  onBeforeUnmount,
  nextTick,
  shallowRef,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import noItens from '@/assets/images/noItens.png'
import noResult from '@/assets/images/noResults.png'
import type { GenericColumns } from '@/types/table'
import type { DataTableHeader } from 'vuetify'
import type { CSSProperties } from 'vue'

const { t } = useI18n()
const { mobile } = useDisplay()
const isMobile = computed(() => mobile.value)

type SortOrder = 'asc' | 'desc'
type SortItem = { key: string; order: SortOrder }

type UpdatePayload = {
  page: number
  itemsPerPage: number
  searchQuery: string
  orderBy: string
  order: SortOrder
}

const emit = defineEmits<{
  (e: 'delete-item', item: T): void
  (e: 'detail-item', item: T): void
  (e: 'update', payload: UpdatePayload): void
  (e: 'selection-change', value: T[]): void
  (e: 'update:modelValue', value: T[]): void
  (e: 'update:searchQuery', value: string): void
  (e: 'update-item', item: T): void
  (e: 'update:currentPage', value: number): void
  (e: 'update:itemsPerPage', value: number): void
}>()

const props = withDefaults(
  defineProps<{
    items?: T[]
    itemsLength?: number
    itemsPerPage?: number
    currentPage: number
    columns?: GenericColumns<T>
    showSelect?: boolean
    selectable?: boolean
    searchable?: boolean
    modelValue?: T[]
    showMassAction?: boolean
    rowClickable?: boolean
    searchQuery?: string
    loading?: boolean
    maxWidth?: string | number
    fitToContainer?: boolean
    importContacts?: boolean
  }>(),
  {
    items: () => [],
    itemsLength: 0,
    itemsPerPage: 30,
    currentPage: 1,
    columns: () => [],
    showSelect: false,
    selectable: false,
    searchable: false,
    modelValue: () => [],
    showMassAction: false,
    rowClickable: true,
    searchQuery: '',
    loading: false,
    maxWidth: undefined,
    fitToContainer: false,
    importContacts: false,
  }
)

/* =========================
   SORT
   ========================= */
const sortBy = ref<SortItem[]>([])

function getSortOrder(key: string): SortOrder | null {
  const s = sortBy.value.find((x) => x.key === key)
  return s?.order ?? null
}

function getSortIcon(key: string): string {
  const order = getSortOrder(key)
  if (order === 'asc') return 'mdi-arrow-down'
  if (order === 'desc') return 'mdi-arrow-up'
  return 'mdi-swap-vertical'
}

function toggleSortByKey(key: string) {
  const current = sortBy.value.find((x) => x.key === key)

  if (!current) {
    sortBy.value = [{ key, order: 'asc' }]

    return
  }

  if (current.order === 'asc') {
    sortBy.value = [{ key, order: 'desc' }]

    return
  }

  sortBy.value = []
  console.log('SORT -> none', sortBy.value)
}

type MaybeWithId = Record<string, unknown> & { id?: string | number }
function hasId(obj: unknown): obj is MaybeWithId {
  if (obj == null || typeof obj !== 'object') return false
  const o = obj as { id?: unknown }
  return typeof o.id === 'string' || typeof o.id === 'number'
}

/* =========================
   SELECTION
   ========================= */
const internalSelection: Ref<T[]> = ref([])

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal !== internalSelection.value) {
      internalSelection.value = newVal ?? []
    }
  },
  { immediate: true }
)

watch(internalSelection, (val) => {
  emit('update:modelValue', val)
  emit('selection-change', val)
})

type RowItem<U> = { raw: U } | U
function unwrapRowItem(rowItem: RowItem<T>): T {
  if (rowItem != null && typeof rowItem === 'object' && 'raw' in rowItem) {
    return (rowItem as { raw: T }).raw
  }
  return rowItem as T
}

function isSelectedRow(rowItem: RowItem<T>): boolean {
  const item = unwrapRowItem(rowItem)
  if (!hasId(item)) return false
  return internalSelection.value.some((s) => hasId(s) && s.id === item.id)
}

function toggleRow(rowItem: RowItem<T>, checked: boolean) {
  const item = unwrapRowItem(rowItem)
  if (!hasId(item)) return

  if (checked) {
    if (!internalSelection.value.some((s) => hasId(s) && s.id === item.id)) {
      internalSelection.value = [...internalSelection.value, item]
    }
    return
  }

  internalSelection.value = internalSelection.value.filter(
    (s) => !(hasId(s) && s.id === item.id)
  )
}

function toggleAllSelection(checked: boolean) {
  internalSelection.value = checked ? [...(props.items ?? [])] : []
}

const isAllSelected = computed(() => {
  if (!props.items?.length || !internalSelection.value.length) return false
  return props.items.every(
    (i) =>
      hasId(i) && internalSelection.value.some((s) => hasId(s) && s.id === i.id)
  )
})

const isSomeSelected = computed(() => {
  if (!props.items?.length || !internalSelection.value.length) return false
  return (
    props.items.some(
      (i) =>
        hasId(i) &&
        internalSelection.value.some((s) => hasId(s) && s.id === i.id)
    ) && !isAllSelected.value
  )
})

type RowPropsArg<U> = { item: RowItem<U> }
function rowProps({ item }: RowPropsArg<T>): { class: string; style?: string } {
  const selected = isSelectedRow(item)
  return {
    class: selected ? 'row-selected' : '',
    style: selected ? 'background-color:#F1F8E9;' : undefined,
  }
}

/* =========================
   SEARCH + UPDATE REQUESTS
   ========================= */
const search = ref<string>(props.searchQuery ?? '')

const pending = ref(false)
const isBusy = computed(() => Boolean(props.loading) || pending.value)

const emptyGate = ref(false)
let emptyGateTimer: number | undefined

function closeEmptyGate() {
  emptyGate.value = false
  window.clearTimeout(emptyGateTimer)
  emptyGateTimer = undefined
}

function openEmptyGateWithDelay(ms = 200) {
  window.clearTimeout(emptyGateTimer)
  emptyGateTimer = window.setTimeout(() => {
    emptyGate.value = true
  }, ms) as unknown as number
}

watch(
  isBusy,
  (busy) => {
    if (busy) closeEmptyGate()
    else openEmptyGateWithDelay(200)
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  window.clearTimeout(emptyGateTimer)
})

const pendingSince = ref<number | null>(null)

function requestUpdate(payload: UpdatePayload) {
  pending.value = true
  pendingSince.value = window.performance.now()
  emit('update', payload)
}

watch(
  () => props.loading,
  (val) => {
    if (val === false) {
      pending.value = false
      pendingSince.value = null
    }
  }
)

let searchTimer: number | undefined
watch(
  search,
  (val) => {
    window.clearTimeout(searchTimer)
    searchTimer = window.setTimeout(() => {
      emit('update:searchQuery', val)
      requestUpdate({
        page: 1,
        itemsPerPage: props.itemsPerPage,
        searchQuery: val,
        orderBy: sortBy.value[0]?.key ?? '',
        order: sortBy.value[0]?.order ?? 'asc',
      })
    }, 250) as unknown as number
  },
  { flush: 'post' }
)

watch(
  () => props.searchQuery,
  (val) => {
    const safe = val ?? ''
    if (safe !== search.value) search.value = safe
  }
)

watch(sortBy, (newVal, oldVal) => {
  const current = newVal[0]
  const previous = oldVal?.[0]

  if (
    current?.key === previous?.key &&
    current?.order === previous?.order &&
    newVal.length === oldVal.length
  ) {
    return
  }

  emit('update:currentPage', 1)

  requestUpdate({
    page: 1,
    itemsPerPage: props.itemsPerPage,
    orderBy: current?.key ?? '',
    order: current?.order ?? 'asc',
    searchQuery: search.value,
  })
})

/* =========================
   EMPTY STATE / TABLE VISIBILITY
   ========================= */
const lastItems = shallowRef<ReadonlyArray<T>>([])

watch(
  () => props.items,
  (val) => {
    if (Array.isArray(val) && val.length > 0) lastItems.value = [...val]
  },
  { immediate: true }
)

const displayedItems = computed<ReadonlyArray<T>>(() => {
  const current: ReadonlyArray<T> = props.items ?? []
  if (isBusy.value && current.length === 0) return lastItems.value
  return current
})

const itemsCount = computed(() => {
  if (isBusy.value) return displayedItems.value.length
  const len = props.itemsLength
  if (typeof len === 'number') return len
  return displayedItems.value.length
})

const hasItems = computed(() => itemsCount.value > 0)
const hasSearch = computed(() => (search.value ?? '').trim().length > 0)

const showSearchBar = computed(
  () => props.searchable && (hasItems.value || hasSearch.value)
)

const showEmptyA = computed(
  () => emptyGate.value && !isBusy.value && !hasSearch.value && !hasItems.value
)

const showEmptyB = computed(
  () => emptyGate.value && !isBusy.value && hasSearch.value && !hasItems.value
)

const showTable = computed(() => hasItems.value || isBusy.value)

/* =========================
   HEADERS
   ========================= */
const headers = computed<DataTableHeader[]>(() =>
  props.columns.map((column) => ({
    title: column.title,
    key: String(column.key),
    sortable: column.sortable ?? true,
    width: column.width || '150px',
  }))
)

/* =========================
   TABLE WIDTH / SCROLL
   ========================= */
const tableScrollStyle = computed<CSSProperties>(() => {
  const mw = props.maxWidth
  if (mw == null || mw === '') return { width: '100%' }
  return { width: '100%', maxWidth: typeof mw === 'number' ? `${mw}px` : mw }
})

const scrollEl = ref<HTMLElement | null>(null)
const containerWidth = ref(0)

function parsePx(v: unknown): number {
  if (typeof v === 'number') return v
  if (typeof v !== 'string') return 0
  const n = Number(v.replace('px', '').trim())
  return Number.isFinite(n) ? n : 0
}

const effectiveMaxPx = computed(() => {
  const mw = props.maxWidth
  if (mw == null || mw === '') return Number.POSITIVE_INFINITY
  return typeof mw === 'number' ? mw : parsePx(mw)
})

const fallbackColPx = 160
function widthToPx(
  width: unknown,
  container: number,
  fallback: number
): number {
  if (typeof width === 'number') return width
  if (typeof width === 'string') {
    const v = width.trim()
    if (v.endsWith('px')) {
      const n = Number(v.replace('px', '').trim())
      return Number.isFinite(n) ? n : fallback
    }
    if (v.endsWith('%')) {
      const p = Number(v.replace('%', '').trim())
      if (Number.isFinite(p) && container > 0) return (container * p) / 100
      return fallback
    }
  }
  return fallback
}

const columnsTotalPx = computed(() => {
  const base =
    props.columns?.reduce((acc, column) => {
      const minWidth = (column as { minWidth?: number }).minWidth
      if (typeof minWidth === 'number' && minWidth > 0) {
        return acc + minWidth
      }

      return acc + widthToPx(column.width, containerWidth.value, fallbackColPx)
    }, 0) ?? 0

  const extra = (props.selectable ? 56 : 0) + 24 + 24
  return base + extra
})

const minTableWidthPx = computed(() =>
  Math.min(columnsTotalPx.value, effectiveMaxPx.value)
)

const shouldScrollX = computed(() => {
  if (!props.fitToContainer) return true
  return minTableWidthPx.value > containerWidth.value + 1
})

const shellStyle = computed<CSSProperties>(() => ({
  ...tableScrollStyle.value,
  '--min-table-w': `${minTableWidthPx.value}px`,
}))

let ro: ResizeObserver | null = null
onMounted(() => {
  if (!scrollEl.value) return
  ro = new ResizeObserver((entries) => {
    const w = entries[0]?.contentRect?.width ?? 0
    containerWidth.value = Math.floor(w)
  })
  ro.observe(scrollEl.value)
  nextTick(() => {})
})

onBeforeUnmount(() => {
  ro?.disconnect()
  ro = null
})

/* =========================
   Table wrap min height
   ========================= */
const viewportH = ref(window.innerHeight)
function onResize() {
  viewportH.value = window.innerHeight
}

const massActionsActive = computed(
  () => Boolean(props.showMassAction) && internalSelection.value.length > 0
)

const layoutOffsetPx = computed(() => (massActionsActive.value ? 280 : 240))

const tableWrapStyle = computed<CSSProperties>(() => ({
  minHeight: `${Math.max(
    320,
    viewportH.value - layoutOffsetPx.value - (props.importContacts ? 310 : 120)
  )}px`,
}))

onMounted(() => window.addEventListener('resize', onResize))
onBeforeUnmount(() => window.removeEventListener('resize', onResize))

/* =========================
   Header tooltip
   ========================= */
const tooltipText = ref('')
const tooltipVisible = ref(false)
const tooltipX = ref(0)
const tooltipY = ref(0)

type HeaderHoverTarget = HTMLElement & {
  dataset: DOMStringMap & { headerTitle?: string }
}

function isTruncated(el: HTMLElement): boolean {
  return el.scrollWidth > el.clientWidth + 1
}

function showHeaderTooltip(targetEl: HeaderHoverTarget) {
  const title = targetEl.dataset.headerTitle?.trim() ?? ''
  if (!title) return
  if (!isTruncated(targetEl)) return
  const rect = targetEl.getBoundingClientRect()
  tooltipText.value = title
  tooltipVisible.value = true
  tooltipX.value = rect.left + rect.width / 2
  tooltipY.value = rect.top - 8
}

function hideHeaderTooltip() {
  tooltipVisible.value = false
  tooltipText.value = ''
}

function onHeaderMouseOver(ev: MouseEvent) {
  const target = ev.target as HTMLElement | null
  if (!target) return
  const el = target.closest('.dt-header-title') as HeaderHoverTarget | null
  if (!el) return
  showHeaderTooltip(el)
}

function onHeaderMouseOut(ev: MouseEvent) {
  const target = ev.target as HTMLElement | null
  if (!target) return
  const el = target.closest('.dt-header-title') as HeaderHoverTarget | null
  if (!el) return
  hideHeaderTooltip()
}

function onHeaderMouseMove(ev: MouseEvent) {
  if (!tooltipVisible.value) return
  tooltipX.value = ev.clientX
  tooltipY.value = ev.clientY - 12
}

function onWindowScrollOrResize() {
  if (tooltipVisible.value) hideHeaderTooltip()
}

onMounted(() => {
  window.addEventListener('scroll', onWindowScrollOrResize, true)
  window.addEventListener('resize', onWindowScrollOrResize, true)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onWindowScrollOrResize, true)
  window.removeEventListener('resize', onWindowScrollOrResize, true)
})

/* =========================
   Row click
   ========================= */
function handleRowClick(_: MouseEvent, data: { item: unknown }) {
  const row = unwrapRowItem(data.item as RowItem<T>)
  if (hasId(row)) emit('detail-item', row)
}

function handleConditionalRowClick(event: MouseEvent, data: { item: unknown }) {
  if (props.rowClickable) handleRowClick(event, data)
}

/* =========================
   Safe input directive
   ========================= */
type SafeRole = 'search' | 'text' | 'email' | 'url' | 'tel' | 'none'
type SafeBinding = { role?: SafeRole; name?: string; id?: string } | undefined

function applySafeAttrs(
  target: HTMLInputElement | HTMLTextAreaElement,
  cfg?: SafeBinding
) {
  target.setAttribute('autocomplete', 'off')
  target.setAttribute('autocapitalize', 'off')
  target.setAttribute('autocorrect', 'off')
  target.setAttribute('spellcheck', 'false')
  if (cfg?.name) target.setAttribute('name', cfg.name)
  if (cfg?.id) target.setAttribute('id', cfg.id)
  target.setAttribute('data-gramm', 'false')
  target.setAttribute('data-lpignore', 'true')
  target.setAttribute('data-1p-ignore', 'true')
  target.setAttribute('data-form-type', 'other')
  if (cfg?.role === 'search') target.setAttribute('inputmode', 'search')
}

const vSafeInput: Directive<HTMLElement, SafeBinding> = {
  mounted(el, binding) {
    const input = el.querySelector('input, textarea') as
      | HTMLInputElement
      | HTMLTextAreaElement
      | null
    if (input) applySafeAttrs(input, binding.value)
  },
  updated(el, binding) {
    const input = el.querySelector('input, textarea') as
      | HTMLInputElement
      | HTMLTextAreaElement
      | null
    if (input) applySafeAttrs(input, binding.value)
  },
}

/* =========================
   Pagination handlers
   ========================= */
function onUpdatePage(page: number) {
  emit('update:currentPage', page)
  requestUpdate({
    page,
    itemsPerPage: props.itemsPerPage,
    orderBy: sortBy.value[0]?.key ?? '',
    order: sortBy.value[0]?.order ?? 'asc',
    searchQuery: search.value,
  })
}

function onUpdateItemsPerPage(ipp: number) {
  emit('update:itemsPerPage', ipp)
  emit('update:currentPage', 1)
  requestUpdate({
    page: 1,
    itemsPerPage: ipp,
    orderBy: sortBy.value[0]?.key ?? '',
    order: sortBy.value[0]?.order ?? 'asc',
    searchQuery: search.value,
  })
}

const itemsSignature = computed(() => {
  const ids = (props.items ?? []).map((it) => (hasId(it) ? String(it.id) : ''))
  return `${ids.join('|')}::${String(props.itemsLength ?? '')}`
})

function getCellValue(item: unknown, key: string): unknown {
  if (item && typeof item === 'object' && key in item) {
    return (item as Record<string, unknown>)[key]
  }
  return undefined
}

watch(
  itemsSignature,
  () => {
    if (!pending.value) return
    pending.value = false
    pendingSince.value = null
  },
  { flush: 'post' }
)
</script>

<template>
  <div
    ref="scrollEl"
    class="table-shell"
    :class="{
      'no-x-scroll': props.fitToContainer && !shouldScrollX,
      'x-scroll': !props.fitToContainer || shouldScrollX,
      'has-mass-actions': massActionsActive,
    }"
    :style="shellStyle"
  >
    <teleport to="body">
      <div
        v-show="tooltipVisible"
        class="dt-header-tooltip"
        :style="{ left: `${tooltipX}px`, top: `${tooltipY}px` }"
        role="tooltip"
        aria-live="polite"
      >
        {{ tooltipText }}
      </div>
    </teleport>

    <div v-if="showSearchBar" class="table-search">
      <div class="search-left d-flex align-center">
        <v-text-field
          v-model="search"
          v-safe-input="{ role: 'search', name: 'eve-search' }"
          variant="underlined"
          hide-details
          append-inner-icon="mdi-magnify"
          :placeholder="t('general.searchButton')"
          style="max-width: 400px"
        />
        <slot name="search-append" />
      </div>
      <slot name="selected" />
    </div>

    <div
      class="mass-actions"
      :class="{ 'mass-actions--active': massActionsActive }"
    >
      <slot name="mass-actions-content" />
    </div>

    <template v-if="showEmptyA">
      <slot name="empty-a">
        <div class="empty-state empty-state--full">
          <div class="empty-content">
            <div class="empty-visual">
              <img :src="noItens" alt="Empty illustration" />
            </div>
            <h3 class="text-h6 font-weight-semibold text-grey-darken-3 mb-1">
              {{ t('general.nothingHere') }}
            </h3>
            <p class="text-body-2 empty-desc mb-0">
              {{ t('general.createFirstItem') }}
            </p>
          </div>
        </div>
      </slot>
    </template>

    <template v-else-if="showEmptyB">
      <slot name="empty-b">
        <div class="empty-state empty-state--full">
          <div class="empty-content">
            <div class="empty-visual">
              <img :src="noResult" alt="No results illustration" />
            </div>
            <h3 class="text-h6 font-weight-semibold text-grey-darken-3 mb-0">
              {{ t('general.tryAnotherSearch') }}
            </h3>
          </div>
        </div>
      </slot>
    </template>

    <template v-else-if="showTable">
      <div
        class="table-wrap"
        :class="{ 'table-wrap--with-mass-actions': massActionsActive }"
        :style="tableWrapStyle"
      >
        <div v-if="isBusy" class="dt-loading-overlay">
          <v-progress-circular indeterminate size="28" />
          <span class="text-body-2">{{ t('general.loading') }}</span>
        </div>

        <v-data-table-server
          v-model="internalSelection"
          :headers="headers"
          :items="displayedItems"
          :items-length="props.itemsLength"
          :items-per-page="props.itemsPerPage"
          :page="props.currentPage"
          :row-props="rowProps"
          :show-select="props.selectable"
          :loading="props.loading"
          item-value="id"
          class="data-table ma-0 flex-grow"
          :items-per-page-text="t('general.itemsPerPage')"
          :items-per-page-options="isMobile ? [10, 20] : [10, 30, 50, 100]"
          density="comfortable"
          page-text="{0}-{1} de {2}"
          return-object
          @click:row="handleConditionalRowClick"
          @update:page="onUpdatePage"
          @update:items-per-page="onUpdateItemsPerPage"
        >
          <template #header.data-table-select>
            <div class="d-flex justify-center">
              <v-checkbox
                class="dt-checkbox"
                density="compact"
                hide-details
                color="#4CAF50"
                :model-value="isAllSelected"
                :indeterminate="isSomeSelected"
                @update:modelValue="
                  (val: boolean | null) => toggleAllSelection(val === true)
                "
              />
            </div>
          </template>

          <template #item.data-table-select="{ item }">
            <div class="d-flex justify-center">
              <v-checkbox
                class="dt-checkbox"
                density="compact"
                hide-details
                color="primary"
                :model-value="isSelectedRow(item)"
                @click.stop
                @update:modelValue="
                  (val: boolean | null) => toggleRow(item, val === true)
                "
              />
            </div>
          </template>

          <template
            v-for="column in props.columns"
            :key="`h-${String(column.key)}`"
            #[`header.${String(column.key)}`]="{ column: col }"
          >
            <div
              class="dt-header-cell"
              :class="{
                'dt-header-cell--sortable': col.sortable,
                'dt-header-cell--sorted': !!getSortOrder(String(col.key)),
              }"
              @click="col.sortable && toggleSortByKey(String(col.key))"
              @mouseover="onHeaderMouseOver"
              @mouseout="onHeaderMouseOut"
              @mousemove="onHeaderMouseMove"
            >
              <span
                class="dt-header-title"
                :data-header-title="String(col.title ?? '')"
              >
                {{ col.title }}
              </span>

              <v-icon
                v-if="col.sortable"
                class="dt-sort-icon"
                :icon="getSortIcon(String(col.key))"
                size="16"
              />
            </div>
          </template>

          <template
            v-for="column in props.columns"
            :key="String(column.key)"
            #[`item.${String(column.key)}`]="{ item }"
          >
            <slot :name="`item.${String(column.key)}`" :item="item">
              {{
                hasId(item)
                  ? String(getCellValue(item, String(column.key)) ?? '--')
                  : '--'
              }}
            </slot>
          </template>
        </v-data-table-server>
      </div>
    </template>
  </div>
</template>

<style scoped>
:deep(.dt-header-title) {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-width: 0;
  color: var(--opacity-text--primary, rgba(0, 0, 0, 0.87));
  font-family:
    'Open Sans',
    system-ui,
    -apple-system,
    'Segoe UI',
    Roboto,
    Arial,
    sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
}

.dt-header-tooltip {
  position: fixed;
  transform: translate(-50%, -100%);
  z-index: 9999;
  max-width: min(420px, calc(100vw - 24px));
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(17, 17, 17, 0.92);
  color: #fff;
  font-family:
    'Open Sans',
    system-ui,
    -apple-system,
    'Segoe UI',
    Roboto,
    Arial,
    sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
  pointer-events: none;
}

:deep(.v-data-table__wrapper),
:deep(.v-table__wrapper) {
  overflow-x: auto !important;
  overflow-y: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}

:deep(.v-data-table__wrapper > table),
:deep(.v-table__wrapper > table) {
  width: 100% !important;
  table-layout: fixed !important;
}

:deep(.v-data-table__wrapper th),
:deep(.v-data-table__wrapper td),
:deep(.v-table__wrapper th),
:deep(.v-table__wrapper td) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.v-data-table__wrapper td > *),
:deep(.v-data-table__wrapper th > *),
:deep(.v-table__wrapper td > *),
:deep(.v-table__wrapper th > *) {
  max-width: 100%;
  min-width: 0;
}

:deep(tr.row-selected),
:deep(tr.row-selected > td),
:deep(tr.row-selected:hover > td) {
  background-color: #f1f8e9 !important;
}

:deep(.v-data-table tbody tr:not(.row-selected):hover > td) {
  background-color: rgba(0, 0, 0, 0.02);
}

:deep(.v-data-table tbody tr > td) {
  transition: background-color 120ms ease;
}

.dt-checkbox {
  margin: 0 !important;
  padding: 0 !important;
}

:deep(.v-data-table-footer) {
  justify-content: flex-end !important;
}

@media (max-width: 600px) {
  :deep(.v-data-table-footer) {
    justify-content: center !important;
  }
}

:deep(input::placeholder) {
  font-weight: 600;
}

.mass-actions {
  padding: 0;
  margin: 0 0 16px 0;
}

.mass-actions--active {
  padding: 16px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.table-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  padding-bottom: 24px;
}

.table-wrap--with-mass-actions {
  padding-top: 16px;
}

:deep(.data-table) {
  display: flex;
  flex-direction: column;
  height: 100%;
}

:deep(.data-table .v-table__wrapper),
:deep(.data-table .v-data-table__wrapper) {
  flex: 1 1 auto;
}

:deep(.data-table .v-data-table-footer) {
  margin-top: auto;
}

:deep(.dt-header-cell) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  min-width: 0;
}

:deep(.dt-header-cell--sortable) {
  cursor: pointer;
}

:deep(.dt-sort-icon) {
  opacity: 0;
  transition: opacity 0.2s ease;
}

/* aparece no hover */
:deep(.dt-header-cell--sortable:hover .dt-sort-icon) {
  opacity: 1;
}

/* mantém visível quando já está ordenado */
:deep(.dt-header-cell--sorted .dt-sort-icon) {
  opacity: 1;
}

.dt-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(2px);
}

.table-shell {
  width: 100%;
}

.empty-state--full {
  width: 100%;
  background: #fff;
  padding: 16px 16px 24px 16px;
  box-sizing: border-box;
  min-height: calc(100vh - 320px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  max-width: 560px;
  width: 100%;
}

.empty-visual {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-visual img {
  max-width: min(420px, 80vw);
  height: auto;
  display: block;
}

.empty-desc {
  line-height: 1.625;
  color: var(--v-theme-grey-darken-1);
}
</style>
